import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Box,
  TextField,
  Alert,
  Select,
  MenuItem,
  ListItemText,
  FormControl,
  InputLabel
} from '@mui/material';
import type { RolloutSetupOptions, SetupTaskOptions, UserGroup, ProgramGroup } from '@finvari/deliver';
import { effectiveGuardrails } from '@finvari/deliver';
import { useConfig } from '../contexts/ConfigContext';
import { useConfiguredEnvironments } from '../contexts/EnvironmentsContext';
import { usePools } from '../contexts/PoolsContext';

export interface DeploymentOptionsDialogProps {
  open: boolean;
  onClose: () => void;
  environmentId: string;
  environmentName?: string;
  // When deploying to a pool there is no concrete environment yet; the pool's guardrails stand in
  // for the (homogeneous) member that will be leased.
  poolId?: string;
  onDeployOnly?: (selectedModules?: string[], autoDeployOnPush?: boolean) => void;
  onDeployAndPrep?: (options: RolloutSetupOptions, selectedModules?: string[], autoDeployOnPush?: boolean) => void;
  // For "retry setup" mode - when set, dialog shows only setup options with a single confirm button
  mode?: 'deploy' | 'retrySetup';
  initialOptions?: SetupTaskOptions;
  onRetrySetup?: (options: SetupTaskOptions) => void;
  availableModules?: string[];
}

const STORAGE_KEY_PREFIX = 'setupOptions:';
const LOAD_BACKUP_PHRASE = 'LOAD EXPENSIVE FULL BACKUP';
// Restoring a full production backup is disabled for now. The backend support remains, but the
// control is hidden and never requested. Flip to true to re-enable the UI.
const ALLOW_FULL_BACKUP_RESTORE = false;
const CUSTOM_GROUP_ID = '__custom__';
// Environments without customer-snapshot access may only load demo data. We pin "Programs to load"
// to this configured group (falling back to the literal 'demo' program if it isn't configured).
const DEMO_ONLY_GROUP_ID = 'demo-only';
const DEMO_ONLY_FALLBACK_PROGRAMS = 'demo';

interface StoredOptions extends Partial<RolloutSetupOptions> {
  selectedUserGroupId?: string;
  selectedProgramGroupId?: string;
}

function getStoredOptions(environmentId: string): StoredOptions | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PREFIX + environmentId);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function storeOptions(environmentId: string, options: StoredOptions): void {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + environmentId, JSON.stringify(options));
  } catch {
    // Ignore storage errors
  }
}

type GroupEntry<T> = { id: string; group: T };

function sortGroupsByName<T extends { name: string }>(
  groups: Record<string, T> | undefined
): GroupEntry<T>[] {
  if (!groups) return [];
  return Object.entries(groups)
    .map(([id, group]) => ({ id, group }))
    .sort((a, b) => a.group.name.localeCompare(b.group.name));
}

export default function DeploymentOptionsDialog({
  open,
  onClose,
  environmentId,
  environmentName,
  poolId,
  onDeployOnly,
  onDeployAndPrep,
  mode = 'deploy',
  initialOptions,
  onRetrySetup,
  availableModules,
}: DeploymentOptionsDialogProps) {
  const { config, userProfile } = useConfig();
  const { environmentsById } = useConfiguredEnvironments();
  const { poolsById, membersByPoolId } = usePools();

  const [wipeData, setWipeData] = useState(false);
  const [loadFullBackup, setLoadFullBackup] = useState('');
  const [loadProgramsText, setLoadProgramsText] = useState('');
  const [adminEmailsText, setAdminEmailsText] = useState('');
  const [automaticallyRunDefaultTests, setAutomaticallyRunDefaultTests] = useState(false);
  const [autoDeployOnPush, setAutoDeployOnPush] = useState(false);
  const [deployAllModules, setDeployAllModules] = useState(true);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [selectedUserGroupId, setSelectedUserGroupId] = useState<string>(CUSTOM_GROUP_ID);
  const [selectedProgramGroupId, setSelectedProgramGroupId] = useState<string>(CUSTOM_GROUP_ID);
  const defaultUserGroupID = config?.userGroups?.['default'] ? 'default' : CUSTOM_GROUP_ID;
  const defaultProgramGroupID = config?.programGroups?.['default'] ? 'default' : CUSTOM_GROUP_ID;

  // Sort groups alphabetically by name
  const sortedUserGroups = useMemo(() => sortGroupsByName(config?.userGroups), [config?.userGroups]);
  const sortedProgramGroups = useMemo(() => sortGroupsByName(config?.programGroups), [config?.programGroups]);

  const hasUserGroups = sortedUserGroups.length > 0;
  const hasProgramGroups = sortedProgramGroups.length > 0;

  // Whether the target is protected. A pool deploy has no concrete environment yet and is assigned
  // whichever member is free, so it counts as protected when ANY member is — matching
  // assertMayDeployToPool on the backend, which refuses a pool the caller can't deploy to entirely.
  const isProtectedEnvironment = useMemo(() => {
    const protectedIds = config?.protectedEnvironments;
    if (!protectedIds?.length) return false;
    if (poolId) {
      return (membersByPoolId[poolId] ?? []).some((env) => protectedIds.includes(env.gcpProjectId));
    }
    return protectedIds.includes(environmentId);
  }, [config?.protectedEnvironments, environmentId, poolId, membersByPoolId]);

  // Compute user permissions for this environment
  const canDeploy = !isProtectedEnvironment || (userProfile?.canDeployToProtectedEnvironments ?? false);
  const userCanResetData = !isProtectedEnvironment || (userProfile?.canEditProtectedEnvironments ?? false);
  // Hard guardrail source: for a pool deploy the concrete environment isn't chosen yet, so the
  // pool's guardrails (which every member is forced to match) stand in; otherwise the target env's.
  const guardrails = effectiveGuardrails(poolId ? poolsById[poolId] : environmentsById[environmentId]);
  // When canResetData is false, destructive data operations are unavailable to everyone.
  const envAllowsReset = guardrails.canResetData;
  // Gate on userCanResetData (not canEditProtectedEnvironments directly) so the destructive
  // options stay available to regular users on non-protected environments, mirroring the
  // backend check in createSetupTaskForRollout.
  const canUseExpensiveThings = userCanResetData && envAllowsReset;
  // Without customer-snapshot access, the programs-to-load selection is pinned to the demo-only group.
  const lockProgramsToDemo = !guardrails.canLoadCustomerData;

  // Get current group definitions
  const getSelectedUserGroup = (groupId: string): UserGroup | undefined =>
    groupId !== CUSTOM_GROUP_ID ? config?.userGroups?.[groupId] : undefined;

  const getSelectedProgramGroup = (groupId: string): ProgramGroup | undefined =>
    groupId !== CUSTOM_GROUP_ID ? config?.programGroups?.[groupId] : undefined;

  // Handle user group selection change
  const handleUserGroupChange = (groupId: string) => {
    setSelectedUserGroupId(groupId);
    const group = getSelectedUserGroup(groupId);
    if (group) {
      setAdminEmailsText(group.emails.join(', '));
    }
  };

  // Handle program group selection change
  const handleProgramGroupChange = (groupId: string) => {
    setSelectedProgramGroupId(groupId);
    const group = getSelectedProgramGroup(groupId);
    if (group) {
      setLoadProgramsText(group.programIds.join(', '));
    }
  };

  // Reset module selection and the per-deploy autodeploy intent when the dialog opens. Autodeploy is
  // a fresh choice each deploy (not remembered like the setup preferences), so it always starts off.
  useEffect(() => {
    if (open) {
      setDeployAllModules(true);
      setAutoDeployOnPush(false);
      if (availableModules) {
        setSelectedModules([...availableModules]);
      }
    }
  }, [open, availableModules]);

  // Load stored preferences when dialog opens (or initial options for retry mode)
  useEffect(() => {
    if (open) {
      if (mode === 'retrySetup' && initialOptions) {
        // Use provided initial options for retry mode
        setWipeData(initialOptions.wipeData ?? false);
        setLoadFullBackup(initialOptions.loadFullBackup ? LOAD_BACKUP_PHRASE : '');
        setAutomaticallyRunDefaultTests(false); // Not applicable for retry

        // Handle user group: check if stored group still exists
        if (initialOptions.selectedUserGroupId && config?.userGroups?.[initialOptions.selectedUserGroupId]) {
          setSelectedUserGroupId(initialOptions.selectedUserGroupId);
          const group = config.userGroups[initialOptions.selectedUserGroupId];
          setAdminEmailsText(group.emails.join(', '));
        } else if (initialOptions.adminEmails && initialOptions.adminEmails.length > 0) {
          // Has custom emails - keep them editable via custom group
          setSelectedUserGroupId(CUSTOM_GROUP_ID);
          setAdminEmailsText(initialOptions.adminEmails.join(', '));
        } else {
          setSelectedUserGroupId(defaultUserGroupID);
          setAdminEmailsText('');
        }

        // Handle program group: check if stored group still exists
        if (initialOptions.selectedProgramGroupId && config?.programGroups?.[initialOptions.selectedProgramGroupId]) {
          setSelectedProgramGroupId(initialOptions.selectedProgramGroupId);
          const group = config.programGroups[initialOptions.selectedProgramGroupId];
          setLoadProgramsText(group.programIds.join(', '));
        } else if (initialOptions.loadPrograms && initialOptions.loadPrograms.length > 0) {
          // Has custom programs - keep them editable via custom group
          setSelectedProgramGroupId(CUSTOM_GROUP_ID);
          setLoadProgramsText(initialOptions.loadPrograms.join(', '));
        } else {
          setSelectedProgramGroupId(defaultProgramGroupID);
          setLoadProgramsText('');
        }
      } else {
        // Load from storage for deploy mode
        const stored = getStoredOptions(environmentId);
        if (stored) {
          setWipeData(stored.wipeData ?? false);
          setAutomaticallyRunDefaultTests(stored.automaticallyRunDefaultTests ?? false);

          // Handle user group: check if stored group still exists
          if (stored.selectedUserGroupId && config?.userGroups?.[stored.selectedUserGroupId]) {
            setSelectedUserGroupId(stored.selectedUserGroupId);
            const group = config.userGroups[stored.selectedUserGroupId];
            setAdminEmailsText(group.emails.join(', '));
          } else if (stored.adminEmails && stored.adminEmails.length > 0) {
            // Has custom emails from storage - keep them editable via custom group
            setSelectedUserGroupId(CUSTOM_GROUP_ID);
            setAdminEmailsText(stored.adminEmails.join(', '));
          } else {
            setSelectedUserGroupId(defaultUserGroupID);
            setAdminEmailsText(userProfile?.email ?? '');
          }

          // Handle program group: check if stored group still exists
          if (stored.selectedProgramGroupId && config?.programGroups?.[stored.selectedProgramGroupId]) {
            setSelectedProgramGroupId(stored.selectedProgramGroupId);
            const group = config.programGroups[stored.selectedProgramGroupId];
            setLoadProgramsText(group.programIds.join(', '));
          } else if (stored.loadPrograms && stored.loadPrograms.length > 0) {
            // Has custom programs from storage - keep them editable via custom group
            setSelectedProgramGroupId(CUSTOM_GROUP_ID);
            setLoadProgramsText(stored.loadPrograms.join(', '));
          } else {
            setSelectedProgramGroupId(defaultProgramGroupID);
            setLoadProgramsText('');
          }
        } else {
          // Reset to defaults
          setWipeData(false);
          setLoadFullBackup('');
          setLoadProgramsText('');
          setAdminEmailsText(userProfile?.email ?? '');
          setAutomaticallyRunDefaultTests(false);
          setSelectedUserGroupId(defaultUserGroupID);
          setSelectedProgramGroupId(defaultProgramGroupID);
        }
      }

      // Pin programs to the demo-only group when the environment can't load customer snapshots.
      if (lockProgramsToDemo) {
        const demoGroup = config?.programGroups?.[DEMO_ONLY_GROUP_ID];
        if (demoGroup) {
          setSelectedProgramGroupId(DEMO_ONLY_GROUP_ID);
          setLoadProgramsText(demoGroup.programIds.join(', '));
        } else {
          setSelectedProgramGroupId(CUSTOM_GROUP_ID);
          setLoadProgramsText(DEMO_ONLY_FALLBACK_PROGRAMS);
        }
      }
    }
  }, [open, environmentId, userProfile, mode, initialOptions, config?.userGroups, config?.programGroups, defaultUserGroupID, defaultProgramGroupID, lockProgramsToDemo]);

  const handleDeployOnly = () => {
    const modulesToDeploy = !deployAllModules ? selectedModules : undefined;
    onDeployOnly?.(modulesToDeploy, autoDeployOnPush);
    onClose();
  };

  const handleDeployAndPrep = () => {
    const options: RolloutSetupOptions = {
      wipeData,
      loadFullBackup: ALLOW_FULL_BACKUP_RESTORE && loadFullBackup === LOAD_BACKUP_PHRASE,
      loadPrograms: loadProgramsText
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0),
      adminEmails: adminEmailsText
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0),
      automaticallyRunDefaultTests,
      ...(selectedUserGroupId !== CUSTOM_GROUP_ID && { selectedUserGroupId }),
      ...(selectedProgramGroupId !== CUSTOM_GROUP_ID && { selectedProgramGroupId }),
    };

    // Store preferences for next time (include group IDs)
    storeOptions(environmentId, {
      ...options,
      selectedUserGroupId: selectedUserGroupId !== CUSTOM_GROUP_ID ? selectedUserGroupId : undefined,
      selectedProgramGroupId: selectedProgramGroupId !== CUSTOM_GROUP_ID ? selectedProgramGroupId : undefined,
    });

    const modulesToDeploy = !deployAllModules ? selectedModules : undefined;
    onDeployAndPrep?.(options, modulesToDeploy, autoDeployOnPush);
    onClose();
  };

  const handleRetrySetup = () => {
    const options: SetupTaskOptions = {
      wipeData,
      loadFullBackup: ALLOW_FULL_BACKUP_RESTORE && loadFullBackup === LOAD_BACKUP_PHRASE,
      loadPrograms: loadProgramsText
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0),
      adminEmails: adminEmailsText
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0),
      ...(selectedUserGroupId !== CUSTOM_GROUP_ID && { selectedUserGroupId }),
      ...(selectedProgramGroupId !== CUSTOM_GROUP_ID && { selectedProgramGroupId }),
    };
    onRetrySetup?.(options);
    onClose();
  };

  const dialogTitle = mode === 'retrySetup'
    ? `Retry Setup Task - ${environmentId}`
    : `Deploy to ${environmentName}`;

  const isCustomUserGroup = selectedUserGroupId === CUSTOM_GROUP_ID;
  const isCustomProgramGroup = selectedProgramGroupId === CUSTOM_GROUP_ID;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        {/* Permission alerts */}
        {!canDeploy && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {poolId
              ? 'This pool contains a protected environment, and you do not have permission to deploy to it. Contact an administrator if you need access.'
              : 'You do not have permission to deploy to this protected environment. Contact an administrator if you need access.'}
          </Alert>
        )}
        {canDeploy && !userCanResetData && isProtectedEnvironment && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {poolId
              ? 'This pool contains a protected environment. You can deploy code changes, but you do not have permission to reset or modify data. Only "Deploy Only" is available.'
              : 'This is a protected environment. You can deploy code changes, but you do not have permission to reset or modify data. Only "Deploy Only" is available.'}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {mode === 'retrySetup'
            ? 'Modify the setup options and retry the task.'
            : 'Choose to deploy only, or deploy and prepare the environment with the options below.'}
        </Typography>

        {/* Module Selection */}
        {mode === 'deploy' && availableModules && availableModules.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deployAllModules}
                    onChange={(e) => {
                      setDeployAllModules(e.target.checked);
                      if (e.target.checked) {
                        setSelectedModules([...availableModules]);
                      }
                    }}
                  />
                }
                label="Deploy all modules"
              />
            </FormGroup>
            {!deployAllModules && (
              <Box sx={{ ml: 3, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedModules.length} of {availableModules.length} selected
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      if (selectedModules.length === availableModules.length) {
                        setSelectedModules([]);
                      } else {
                        setSelectedModules([...availableModules]);
                      }
                    }}
                  >
                    {selectedModules.length === availableModules.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                <FormGroup>
                  {availableModules.map((mod) => (
                    <FormControlLabel
                      key={mod}
                      control={
                        <Checkbox
                          checked={selectedModules.includes(mod)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedModules(prev => [...prev, mod]);
                            } else {
                              setSelectedModules(prev => prev.filter(m => m !== mod));
                            }
                          }}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">{mod}</Typography>}
                      sx={{ height: 32 }}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}
          </Box>
        )}

        {canUseExpensiveThings && (
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={wipeData}
                  onChange={(e) => setWipeData(e.target.checked)}
                  disabled={!userCanResetData}
                />
              }
              label="Wipe all existing data"
            />
          </FormGroup>
        )}

        {ALLOW_FULL_BACKUP_RESTORE && canUseExpensiveThings && (
          <TextField
            fullWidth
            label="Load with full data backup from production"
            placeholder={LOAD_BACKUP_PHRASE}
            value={loadFullBackup}
            onChange={(e) => setLoadFullBackup(e.target.value)}
            helperText={`Type '${LOAD_BACKUP_PHRASE}' to load a full backup. This will take hours to complete and raise the cost of this environment above $100/month until wiped.`}
            sx={{ mt: 2 }}
            size="small"
            disabled={!userCanResetData}
          />
        )}

        {/* Program Groups */}
        {lockProgramsToDemo && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This environment can't load customer snapshots, so only demo data may be loaded.
          </Alert>
        )}
        {hasProgramGroups ? (
          <FormControl fullWidth sx={{ mt: 2 }} size="small" disabled={!userCanResetData || lockProgramsToDemo}>
            <InputLabel id="program-group-label">Programs to load</InputLabel>
            <Select
              labelId="program-group-label"
              value={selectedProgramGroupId}
              onChange={(e) => handleProgramGroupChange(e.target.value as string)}
              label="Programs to load"
            >
              {sortedProgramGroups.map(({ id, group }) => (
                <MenuItem key={id} value={id}>
                  <ListItemText primary={group.name} secondary={group.description} />
                </MenuItem>
              ))}
              <MenuItem value={CUSTOM_GROUP_ID}>
                <ListItemText primary="Custom" secondary="Enter program names manually" />
              </MenuItem>
            </Select>
          </FormControl>
        ) : null}

        {(isCustomProgramGroup || !hasProgramGroups) && (
          <TextField
            fullWidth
            label={hasProgramGroups ? 'Custom programs' : 'Programs to load data snapshots for'}
            placeholder="program1, program2, program3"
            value={loadProgramsText}
            onChange={(e) => setLoadProgramsText(e.target.value)}
            helperText="Comma-separated list of program names"
            sx={{ mt: 2 }}
            size="small"
            disabled={!userCanResetData || lockProgramsToDemo}
          />
        )}

        {!isCustomProgramGroup && hasProgramGroups && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
            Programs: {loadProgramsText || '(none)'}
          </Typography>
        )}

        {/* User Groups */}
        {hasUserGroups ? (
          <FormControl fullWidth sx={{ mt: 2 }} size="small" disabled={!userCanResetData}>
            <InputLabel id="user-group-label">Admin emails</InputLabel>
            <Select
              labelId="user-group-label"
              value={selectedUserGroupId}
              onChange={(e) => handleUserGroupChange(e.target.value as string)}
              label="Admin emails"
            >
              {sortedUserGroups.map(({ id, group }) => (
                <MenuItem key={id} value={id}>
                  <ListItemText primary={group.name} secondary={group.description} />
                </MenuItem>
              ))}
              <MenuItem value={CUSTOM_GROUP_ID}>
                <ListItemText primary="Custom" secondary="Enter email addresses manually" />
              </MenuItem>
            </Select>
          </FormControl>
        ) : null}

        {(isCustomUserGroup || !hasUserGroups) && (
          <TextField
            fullWidth
            label={hasUserGroups ? 'Custom admin emails' : 'Admin emails'}
            placeholder="user@example.com, user2@example.com"
            value={adminEmailsText}
            onChange={(e) => setAdminEmailsText(e.target.value)}
            helperText="Comma-separated list of email addresses to pre-configure as admins"
            sx={{ mt: 2 }}
            size="small"
            disabled={!userCanResetData}
          />
        )}

        {!isCustomUserGroup && hasUserGroups && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
            Emails: {adminEmailsText || '(none)'}
          </Typography>
        )}

        {mode === 'deploy' && (
          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={automaticallyRunDefaultTests}
                  onChange={(e) => setAutomaticallyRunDefaultTests(e.target.checked)}
                  disabled={!userCanResetData}
                />
              }
              label="Automatically run default tests after setup completes"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoDeployOnPush}
                  onChange={(e) => setAutoDeployOnPush(e.target.checked)}
                  disabled={!canDeploy}
                />
              }
              label="Autodeploy new commits"
            />
            {autoDeployOnPush && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                This environment will redeploy automatically when new commits land on this source,
                until something else is deployed here.
              </Typography>
            )}
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {mode === 'deploy' ? (
          <>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleDeployOnly} variant="outlined" disabled={!canDeploy || (!deployAllModules && selectedModules.length === 0)}>
              Deploy Only
            </Button>
            <Button
              onClick={handleDeployAndPrep}
              variant="contained"
              color="primary"
              disabled={!canDeploy || !userCanResetData || (!deployAllModules && selectedModules.length === 0)}
            >
              Deploy and Prepare Environment
            </Button>
          </>
        ) : (
          <Button
            onClick={handleRetrySetup}
            variant="contained"
            color="primary"
            disabled={!userCanResetData}
          >
            Retry Setup Task
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
